// src/components/Benutzerverwaltung.js
import React, { useState, useEffect } from "react";
import { 
  getAllUsers, 
  updateUserRole, 
  updateUsername,
  deleteUserAccount,
  checkUsernameExists,
  resetPassword,
  registerUser,
  changePassword
} from "../services/firebaseAuthService";
import "./Benutzerverwaltung.css"; // CSS importieren
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../firebaseConfig";

const Benutzerverwaltung = () => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [currentUser, setCurrentUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  
  // Neuer Benutzer Formular State
  const [newUserForm, setNewUserForm] = useState({
    username: "",
    email: "",
    password: "",
    role: "user"
  });
  
  // Alle Benutzer beim Laden abrufen
  useEffect(() => {
    loadUsers();
    loadCurrentUser();
  }, []);
  
  // Benutzer laden
  const loadUsers = async () => {
    setIsLoading(true);
    try {
      const allUsers = await getAllUsers();
      // Benutzerliste alphabetisch sortieren
      const sortedUsers = allUsers.sort((a, b) => {
        const nameA = a.username || "";
        const nameB = b.username || "";
        return nameA.localeCompare(nameB);
      });
      setUsers(sortedUsers);
    } catch (error) {
      console.error("Fehler beim Laden der Benutzer:", error);
      setMessage({
        text: `Fehler beim Laden der Benutzer: ${error.message}`,
        type: "error"
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Aktuellen Benutzer laden
  const loadCurrentUser = () => {
    const userDataString = localStorage.getItem("currentUser");
    if (userDataString) {
      try {
        setCurrentUser(JSON.parse(userDataString));
      } catch (error) {
        console.error("Fehler beim Parsen der Benutzerdaten:", error);
      }
    }
  };
  
  // Benutzerrolle aktualisieren
  const handleRoleChange = async (userId, newRole) => {
    try {
      await updateUserRole(userId, newRole);
      
      // Benutzerliste aktualisieren
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId ? { ...user, role: newRole } : user
        )
      );
      
      setMessage({
        text: "Benutzerrolle erfolgreich aktualisiert",
        type: "success"
      });
    } catch (error) {
      console.error("Fehler beim Aktualisieren der Rolle:", error);
      setMessage({
        text: `Fehler beim Aktualisieren der Rolle: ${error.message}`,
        type: "error"
      });
    }
  };
  
  // Benutzername bearbeiten starten
  const startEditUsername = (user) => {
    setEditingUser({
      id: user.id,
      username: user.username,
      email: user.email,
      role: user.role
    });
  };
  
  // Benutzername bearbeiten abbrechen
  const cancelEditUsername = () => {
    setEditingUser(null);
  };
  
  // Benutzername speichern
  const saveUsername = async () => {
    if (!editingUser || !editingUser.username.trim()) {
      setMessage({
        text: "Bitte geben Sie einen gültigen Benutzernamen ein",
        type: "error"
      });
      return;
    }

    // Hole den aktuellen User aus der Liste
    const originalUser = users.find(u => u.id === editingUser.id);
    const username = typeof editingUser.username !== 'undefined' && editingUser.username.trim() !== '' ? editingUser.username : (originalUser.username || '');
    const email = typeof editingUser.email !== 'undefined' && editingUser.email.trim() !== '' ? editingUser.email : (originalUser.email || '');
    const role = typeof editingUser.role !== 'undefined' && editingUser.role.trim() !== '' ? editingUser.role : (originalUser.role || '');

    try {
      // Prüfen, ob der Benutzername bereits existiert
      const usernameExists = await checkUsernameExists(username);
      const isOwnUsername = originalUser.username === username;
      if (usernameExists && !isOwnUsername) {
        setMessage({
          text: "Dieser Benutzername ist bereits vergeben",
          type: "error"
        });
        return;
      }

      // Benutzername, E-Mail und Rolle aktualisieren
      await updateUsername(editingUser.id, username, email, role);

      setMessage({
        text: "Benutzerdaten erfolgreich aktualisiert",
        type: "success"
      });
      setEditingUser(null);
    } catch (error) {
      setMessage({
        text: `Fehler beim Aktualisieren der Benutzerdaten: ${error.message}`,
        type: "error"
      });
    }
  };
  
  // Benutzer löschen
  const handleDeleteUser = async (userId, username) => {
    // Bestätigung anfordern
    if (!window.confirm(`Sind Sie sicher, dass Sie den Benutzer "${username}" löschen möchten?`)) {
      return;
    }
    
    try {
      await deleteUserAccount(userId);
      
      // Benutzer aus der Liste entfernen
      setUsers(prevUsers => prevUsers.filter(user => user.id !== userId));
      
      setMessage({
        text: `Benutzer "${username}" wurde erfolgreich gelöscht`,
        type: "success"
      });
    } catch (error) {
      console.error("Fehler beim Löschen des Benutzers:", error);
      setMessage({
        text: `Fehler beim Löschen des Benutzers: ${error.message}`,
        type: "error"
      });
    }
  };
  
  // Passwort zurücksetzen
  const handleResetPassword = async (email) => {
    try {
      await resetPassword(email);
      
      setMessage({
        text: `Passwort-Zurücksetzungslink wurde an ${email} gesendet`,
        type: "success"
      });
    } catch (error) {
      console.error("Fehler beim Zurücksetzen des Passworts:", error);
      setMessage({
        text: `Fehler beim Zurücksetzen des Passworts: ${error.message}`,
        type: "error"
      });
    }
  };
  
  // Neuen Benutzer hinzufügen - Formular aktualisieren
  const handleNewUserChange = (e) => {
    const { name, value } = e.target;
    setNewUserForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Neuen Benutzer erstellen
  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    const { username, email, password, role } = newUserForm;
    
    // Validierung
    if (!username || !email || !password) {
      setMessage({
        text: "Bitte füllen Sie alle Pflichtfelder aus",
        type: "error"
      });
      return;
    }
    
    try {
      // Prüfen, ob der Benutzername bereits existiert
      const usernameExists = await checkUsernameExists(username);
      
      if (usernameExists) {
        setMessage({
          text: "Dieser Benutzername ist bereits vergeben",
          type: "error"
        });
        return;
      }
      
      // Benutzer erstellen
      const newUser = await registerUser(email, password, username, role);
      
      // Zur Benutzerliste hinzufügen
      setUsers(prevUsers => [...prevUsers, newUser]);
      
      // Formular zurücksetzen
      setNewUserForm({
        username: "",
        email: "",
        password: "",
        role: "user"
      });
      
      setMessage({
        text: `Benutzer "${username}" wurde erfolgreich erstellt`,
        type: "success"
      });
    } catch (error) {
      console.error("Fehler beim Erstellen des Benutzers:", error);
      let errorMessage = error.message;
      
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = "Diese E-Mail-Adresse wird bereits verwendet";
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = "Ungültige E-Mail-Adresse";
      } else if (error.code === 'auth/weak-password') {
        errorMessage = "Das Passwort ist zu schwach. Es sollte mindestens 6 Zeichen lang sein";
      }
      
      setMessage({
        text: `Fehler beim Erstellen des Benutzers: ${errorMessage}`,
        type: "error"
      });
    }
  };
  
  // Passwort für Benutzer ohne E-Mail erstellen
  const handleCreatePassword = (user) => {
    setSelectedUser(user);
    setShowPasswordModal(true);
  };
  
  const setUserPassword = async (uid, newPassword) => {
    const functions = getFunctions(app);
    const setUserPasswordFn = httpsCallable(functions, "setUserPassword");
    return await setUserPasswordFn({ uid, newPassword });
  };
  
  // Passwort speichern
  const handleSavePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      setMessage({
        text: "Das Passwort muss mindestens 6 Zeichen lang sein",
        type: "error"
      });
      return;
    }
    try {
      await setUserPassword(selectedUser.id, newPassword);
      setShowPasswordModal(false);
      setSelectedUser(null);
      setNewPassword("");
      setMessage({
        text: "Passwort wurde erfolgreich gesetzt!",
        type: "success"
      });
    } catch (error) {
      setMessage({
        text: `Fehler beim Setzen des Passworts: ${error.message}`,
        type: "error"
      });
    }
  };

  return (
    <div className="benutzerverwaltung-container">
      <h1>Benutzerverwaltung</h1>
      <div className="user-card">
        <h2>Neuen Benutzer erstellen</h2>
        <form className="create-user-form compact" onSubmit={handleCreateUser}>
          <div className="form-row compact-row">
            <div className="form-group compact-group">
              <label>Benutzername:</label>
              <input
                type="text"
                name="username"
                placeholder="Benutzername"
                value={newUserForm.username}
                onChange={handleNewUserChange}
                required
              />
            </div>
            <div className="form-group compact-group">
              <label>E-Mail:</label>
              <input
                type="email"
                name="email"
                placeholder="E-Mail"
                value={newUserForm.email}
                onChange={handleNewUserChange}
                required
              />
            </div>
            <div className="form-group compact-group">
              <label>Passwort:</label>
              <input
                type="password"
                name="password"
                placeholder="Passwort"
                value={newUserForm.password}
                onChange={handleNewUserChange}
                required
              />
            </div>
            <div className="form-group compact-group">
              <label>Rolle:</label>
              <select
                name="role"
                value={newUserForm.role}
                onChange={handleNewUserChange}
              >
                <option value="user">Benutzer</option>
                <option value="admin">Admin</option>
                <option value="superadmin">SuperAdmin</option>
                <option value="intern">Intern</option>
              </select>
            </div>
            <button className="btn btn-primary compact-btn" type="submit">Benutzer erstellen</button>
          </div>
        </form>
      </div>

      {/* Meldungen */}
      {message.text && (
        <div className={`message ${message.type}`}>{message.text}</div>
      )}

      {/* Benutzerliste */}
      <h2>Benutzer</h2>
      {isLoading ? (
        <div className="loading-message">Lade Benutzer...</div>
      ) : (
        <table className="user-table">
          <thead>
            <tr>
              <th>Benutzername</th>
              <th>E-Mail</th>
              <th>Rolle</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td className="user-td">
                  {editingUser && editingUser.id === user.id ? (
                    <input
                      type="text"
                      value={editingUser.username}
                      onChange={e => setEditingUser({ ...editingUser, username: e.target.value })}
                      style={{ marginRight: '8px', padding: '4px 8px', borderRadius: '4px', border: '1px solid #ccc' }}
                    />
                  ) : (
                    user.username
                  )}
                </td>
                <td className="user-td">
                  {editingUser && editingUser.id === user.id ? (
                    <input
                      type="email"
                      value={editingUser.email}
                      onChange={e => setEditingUser({ ...editingUser, email: e.target.value })}
                      style={{ marginRight: '8px', padding: '4px 8px', borderRadius: '4px', border: '1px solid #ccc' }}
                    />
                  ) : (
                    user.email
                  )}
                </td>
                <td className="user-td">
                  {editingUser && editingUser.id === user.id ? (
                    <select
                      value={editingUser.role}
                      onChange={e => setEditingUser({ ...editingUser, role: e.target.value })}
                      style={{ marginRight: '8px', padding: '4px 8px', borderRadius: '4px', border: '1px solid #ccc' }}
                    >
                      <option value="user">Benutzer</option>
                      <option value="admin">Admin</option>
                      <option value="superadmin">SuperAdmin</option>
                      <option value="intern">Intern</option>
                    </select>
                  ) : (
                    <span className={`role-badge role-${user.role === 'extern' ? 'intern' : user.role}`}>
                      {user.role === 'extern' || user.role === 'intern'
                        ? 'Intern'
                        : user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                    </span>
                  )}
                </td>
                <td>
                  <div className="action-buttons">
                    {editingUser && editingUser.id === user.id ? (
                      <>
                        <span className="action-badge action-edit" onClick={saveUsername} style={{marginRight: 4, cursor: 'pointer'}}>Speichern</span>
                        <span className="action-badge action-delete" onClick={cancelEditUsername} style={{cursor: 'pointer'}}>Abbrechen</span>
                      </>
                    ) : (
                      <>
                        <span className="action-badge action-edit" onClick={() => startEditUsername(user)} style={{cursor: 'pointer'}}>
                          ✏️ Bearbeiten
                        </span>
                        {user.email ? (
                          <span className="action-badge action-reset" onClick={() => handleResetPassword(user.email, user.id)} style={{cursor: 'pointer'}}>
                            🔑 Reset-Link senden
                          </span>
                        ) : (
                          <span className="action-badge action-password" onClick={() => handleCreatePassword(user)} style={{cursor: 'pointer'}}>
                            🔑 Passwort setzen
                          </span>
                        )}
                        <span className="action-badge action-delete" onClick={() => handleDeleteUser(user.id, user.username)} style={{cursor: 'pointer'}}>
                          🗑️ Löschen
                        </span>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Modal für Passwort setzen */}
      {showPasswordModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Neues Passwort für {selectedUser?.username}</h3>
            <input
              type="password"
              placeholder="Neues Passwort"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              style={{ marginBottom: 12, padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
            />
            <div className="modal-buttons">
              <button className="save-btn" onClick={handleSavePassword} style={{marginRight: 8}}>Speichern</button>
              <button className="cancel-btn" onClick={() => setShowPasswordModal(false)}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Benutzerverwaltung;